/**
 * MaintenanceOngoingBlocker — full-viewport overlay rendered while a
 * maintenance window is in progress.
 *
 * Behavior:
 *   - Returns null when not ongoing OR when user is on an allowlisted
 *     route (auth flows, profile, billing — same escape-hatch shape as
 *     TrialExpiredBlocker.tsx). Mounted unconditionally in Layout for
 *     that reason — zero visual impact in the off path.
 *   - z-40 so the z-50 sidebar stays interactive (same baseline as
 *     TrialExpiredBlocker — workspace switcher + logout remain usable).
 *   - Backdrop dim + no click-outside-to-dismiss. The user MUST wait
 *     out the window or hit "Refresh when ready" once the operator
 *     ends it (which window.location.reload() does — re-fetches
 *     /api/system/maintenance and the context flips back to off).
 *
 * Allowlist (mirrors TrialExpiredBlocker pattern, with prefix-aware
 * matching for nested routes like /profile/security):
 *   - /login, /register, /forgot-password, /reset-password, /invite
 *     (these only matter defensively — Layout doesn't mount on public
 *     routes, but we leave them in to keep the allowlist symmetric
 *     with the §A.4 spec).
 *   - /profile, /profile/*  (user account self-service)
 *   - /billing, /billing/*  (operator may want users upgrading mid-window)
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Wrench, RefreshCcw } from 'lucide-react';
import { useMaintenance } from '../../contexts/MaintenanceContext';

const ALLOWLIST_EXACT_OR_PREFIX = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/invite',
  '/profile',
  '/billing',
];

function isAllowlisted(pathname: string): boolean {
  return ALLOWLIST_EXACT_OR_PREFIX.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

function formatExpectedBack(d: Date | null): string | null {
  if (!d) return null;
  try {
    return d.toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return d.toISOString();
  }
}

const MaintenanceOngoingBlocker: React.FC = () => {
  const { isOngoing, messageTitle, messageBody, endsAt } = useMaintenance();
  const location = useLocation();

  if (!isOngoing) return null;
  if (isAllowlisted(location.pathname)) return null;

  const title = messageTitle?.trim() || 'Maintenance in progress';
  const body =
    messageBody?.trim() ||
    "We're making improvements to keep things running smoothly. " +
      'Please check back shortly.';
  const expected = formatExpectedBack(endsAt);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Maintenance in progress"
      className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      // Eat clicks at the backdrop so users can't accidentally interact
      // with the page underneath. (Matches TrialExpiredBlocker's
      // implicit-eat behavior — no onClick on the backdrop = no
      // dismissal path.)
    >
      <div className="glass w-full max-w-md rounded-2xl border border-white/20 shadow-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
            <Wrench
              className="w-6 h-6 text-amber-600 dark:text-amber-300"
              aria-hidden="true"
            />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-tight break-words">
              {title}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Service paused for maintenance
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 whitespace-pre-line break-words">
          {body}
        </p>

        {expected && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            <span className="font-semibold text-gray-800 dark:text-gray-200">
              Expected back by:
            </span>{' '}
            {expected}
          </p>
        )}

        <button
          onClick={() => window.location.reload()}
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-purple-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-200"
        >
          <RefreshCcw className="w-4 h-4" aria-hidden="true" />
          Refresh when ready
        </button>

        <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
          You can still access{' '}
          <a
            href="/profile"
            className="text-primary hover:underline"
          >
            your profile
          </a>{' '}
          and{' '}
          <a href="/billing" className="text-primary hover:underline">
            billing
          </a>{' '}
          while we work.
        </p>
      </div>
    </div>
  );
};

export default MaintenanceOngoingBlocker;
